import React, { useEffect, useState } from "react";
import L from "leaflet";
import { Marker, useMap } from "react-leaflet";
import FlightIcon from "@mui/icons-material/Flight";
import { renderToStaticMarkup } from "react-dom/server";

const FlightPathWithElevation = ({ 
  points, 
  options = {}, 
  labels = {}, 
  iconPosition, 
  rotation = 0, 
  onPointClick,
  resetTrigger 
}) => {
  const map = useMap();
  const [localPoints, setLocalPoints] = useState(points);
  const [localIconPosition, setLocalIconPosition] = useState(iconPosition);

  useEffect(() => {
    try {
      setLocalPoints(points);
      setLocalIconPosition(iconPosition);
    } catch (err) {
      console.error("Error updating local points or icon position:", err);
    }
  }, [points, iconPosition]);

  useEffect(() => {
    try {
      if (resetTrigger) {
        setLocalPoints([]);
        setLocalIconPosition(null);
      }
    } catch (err) {
      console.error("Error during reset trigger:", err);
    }
  }, [resetTrigger]);

  const isValidLatLng = ([lat, lng]) =>
    typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng);

  console.log("Input points:", localPoints);
  console.log("Icon position:", localIconPosition);

  useEffect(() => {
    try {
      if (!localPoints || localPoints.length < 2) {
        console.warn("Invalid points array:", localPoints);
        return;
      }

      const validPoints = localPoints.filter(isValidLatLng);
      if (validPoints.length < 2) {
        console.warn("Not enough valid points to draw the path:", validPoints);
        return;
      }

      const generateArcWithElevation = (start, end, numPoints = 100, maxAltitude = 50000) => {
        try {
          const toRadians = (deg) => (deg * Math.PI) / 180;
          const toDegrees = (rad) => (rad * 180) / Math.PI;

          const [lat1, lon1] = [toRadians(start[0]), toRadians(start[1])];
          const [lat2, lon2] = [toRadians(end[0]), toRadians(end[1])];
          const dLon = lon2 - lon1;

          return Array.from({ length: numPoints + 1 }, (_, i) => {
            const f = i / numPoints;
            const A = Math.sin((1 - f) * dLon) / Math.sin(dLon);
            const B = Math.sin(f * dLon) / Math.sin(dLon);

            const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
            const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
            const z = A * Math.sin(lat1) + B * Math.sin(lat2);

            const lat = toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y)));
            const lon = toDegrees(Math.atan2(y, x));
            const altitude = Math.sin(Math.PI * f) * maxAltitude;

            return [lat, lon, altitude];
          });
        } catch (err) {
          console.error("Error generating arc with elevation:", err);
          return [];
        }
      };

      const allArcPoints = validPoints.slice(0, -1).flatMap((start, i) => {
        try {
          const end = validPoints[i + 1];
          return generateArcWithElevation(start, end, 100, options.maxAltitude || 50000);
        } catch (err) {
          console.error("Error generating arc segment:", err);
          return [];
        }
      });

      const flatArcPoints = allArcPoints.map(([lat, lon]) => [lat, lon]);

      let flightPath;
      try {
        flightPath = L.polyline(flatArcPoints, {
          color: options.color || "blue",
          weight: options.weight || 3,
          opacity: options.opacity || 0.8,
          dashArray: options.dashArray || null,
        }).addTo(map);

        flightPath.on("click", (e) => {
          try {
            if (onPointClick) onPointClick(e.latlng);
          } catch (err) {
            console.error("Error in onPointClick callback:", err);
          }
        });
      } catch (err) {
        console.error("Failed to create polyline:", err);
        return;
      }

      let startLabel, endLabel, startCircle, endCircle;

      try {
        startLabel = L.marker(validPoints[0], {
          icon: L.divIcon({
            className: "custom-label",
            html: `<div style="font-size: 12px; color: black;">${labels.start || ""}</div>`,
            iconAnchor: [2, -2],
          }),
        }).addTo(map);
      } catch (err) {
        console.error("Failed to add start label:", err);
      }

      try {
        endLabel = L.marker(validPoints[validPoints.length - 1], {
          icon: L.divIcon({
            className: "custom-label",
            html: `<div style="font-size: 12px; color: black;">${labels.end || ""}</div>`,
            iconAnchor: [2, -2],
          }),
        }).addTo(map);
      } catch (err) {
        console.error("Failed to add end label:", err);
      }

      try {
        startCircle = L.circleMarker(validPoints[0], {
          color: "green",
          radius: 4,
          fillOpacity: 1,
        }).addTo(map);
      } catch (err) {
        console.error("Failed to add start circle:", err);
      }

      try {
        endCircle = L.circleMarker(validPoints[validPoints.length - 1], {
          color: "green",
          radius: 4,
          fillOpacity: 1,
        }).addTo(map);
      } catch (err) {
        console.error("Failed to add end circle:", err);
      }

      return () => {
        try {
          map.removeLayer(flightPath);
          map.removeLayer(startLabel);
          map.removeLayer(endLabel);
          map.removeLayer(startCircle);
          map.removeLayer(endCircle);
        } catch (err) {
          console.error("Error during cleanup:", err);
        }
      };
    } catch (err) {
      console.error("Unhandled error in useEffect (flight path):", err);
    }
  }, [localPoints, map, options, labels, onPointClick]);

  let flightIcon = null;
  try {
    flightIcon = L.divIcon({
      html: renderToStaticMarkup(
        <div
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: "center",
          }}
        >
          <FlightIcon style={{ color: "green", fontSize: "44px" }} />
        </div>
      ),
      className: "custom-flight-icon",
      iconSize: [44, 44],
      iconAnchor: [22, 22],
    });
  } catch (err) {
    console.error("Error creating flight icon:", err);
  }

  if (
    localIconPosition &&
    !isValidLatLng([localIconPosition.lat, localIconPosition.lng])
  ) {
    console.error("Invalid icon position:", localIconPosition);
    return null;
  }

  console.log("rotation here", rotation);

  return (
    <>
      {localIconPosition && flightIcon && (
        <Marker
          position={[localIconPosition.lat, localIconPosition.lng]}
          icon={flightIcon}
        />
      )}
    </>
  );
};

export default FlightPathWithElevation;
